import { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import { Icon } from '@seeqdev/qomponents';
import { FormulaEditorAsync } from '@/formula/FormulaEditorAsync.molecule';
import { EditableText } from '@/core/EditableText.atom';
import { useTranslation } from 'react-i18next';

interface FormulaErrors {
  message: string;
  column: number;
  line: number;
}

interface FormulaWithParameters {
  formula: string;
  parameters: { name: string; identifier: string; item?: { id?: string } }[];
  formulaErrors?: FormulaErrors[];
}

export interface FormulaItemTableOption {
  key: string;
  display: string;
  allFormulas?: FormulaWithParameters[];
  id?: string;
}

export interface FormulaItemTableIF {
  options: FormulaItemTableOption[];
  testId?: string;
  extraClassNames?: string;
  onEditClick?: (key: string) => void;
  onRemoveClick?: (key: string) => void;
  onAdditionalIconClick?: (key: string) => void;
  additionalIcon?: string;
  additionalIconTooltip?: string;
  textEditable?: boolean;
  onTextChange?: (newValue: string, original: FormulaItemTableOption) => void;
  hasErrors?: boolean;
}

export const FormulaItemTable: React.FunctionComponent<FormulaItemTableIF> = (props) => {
  const {
    extraClassNames,
    options,
    testId,
    onEditClick,
    onRemoveClick,
    onAdditionalIconClick,
    additionalIcon,
    additionalIconTooltip,
    onTextChange,
    textEditable,
    hasErrors = false,
  } = props;
  const { t } = useTranslation();

  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  // Expand all rows if hasErrors becomes true
  useEffect(() => {
    if (hasErrors) {
      setExpandedKeys(new Set(options.map((opt) => opt.key)));
    }
  }, [hasErrors, options]);

  const toggleRow = useCallback((option: FormulaItemTableOption) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(option.key)) {
        next.delete(option.key);
      } else {
        next.add(option.key);
      }
      return next;
    });
  }, []);

  return (
    <table
      className={clsx('table table-striped table-condensed mb0 width-100percent', extraClassNames)}
      data-testid={testId}
      tabIndex={0}>
      <tbody>
        {options.map((option) => {
          const isOpen = expandedKeys.has(option.key);
          return (
            <tr key={option.key}>
              <td colSpan={1}>
                <div>
                  <div className="flexColumnContainer flexSpaceBetween gap5">
                    {!textEditable && (
                      <span className="flexGrowShrink">
                        <span className="text-bold">{t('NAME')}:</span> {option.display}
                      </span>
                    )}
                    {textEditable && onTextChange && (
                      <EditableText value={option.display} onUpdate={(newText) => onTextChange(newText, option)} />
                    )}
                    {option.allFormulas && (
                      <Icon
                        icon="fc fc-formula"
                        onClick={() => toggleRow(option)}
                        testId={`${testId}FormulaIcon-${option.key}`}
                        extraClassNames="mt3"
                      />
                    )}
                    {onEditClick && (
                      <Icon
                        icon="fa-pencil"
                        onClick={() => onEditClick(option.key)}
                        testId={`${testId}EditIcon-${option.key}`}
                        extraClassNames="mt3 ml8"
                      />
                    )}
                    {onAdditionalIconClick && additionalIcon && (
                      <Icon
                        icon={additionalIcon}
                        onClick={() => onAdditionalIconClick(option.key)}
                        testId={`${testId}ExtraIcon-${option.key}`}
                        extraClassNames="mt3 ml8"
                        tooltip={additionalIconTooltip && t(additionalIconTooltip)}
                      />
                    )}
                    {onRemoveClick && (
                      <Icon
                        icon="fa-trash"
                        onClick={() => onRemoveClick(option.key)}
                        testId={`${testId}RemoveIcon-${option.key}`}
                        extraClassNames="mt3 ml8"
                      />
                    )}
                  </div>
                  {isOpen && option.allFormulas && (
                    <div>
                      {option.allFormulas.map((formula, idx) => (
                        <div
                          key={idx}
                          className={clsx(idx === 0 && 'mt5', idx !== option.allFormulas!.length - 1 && 'mb12')}>
                          <span className="text-bold">
                            {t(
                              option.allFormulas!.length > 1 ? 'FORMULA.FORMULA_COUNT' : 'FORMULA.FORMULA',
                              option.allFormulas!.length > 1 ? { count: idx + 1 } : undefined,
                            )}
                            :
                          </span>{' '}
                          <FormulaEditorAsync
                            formula={formula.formula}
                            readOnly={true}
                            readOnlyWithErrors={true}
                            showLineNumbers={false}
                            lineWrapping={true}
                            formulaErrors={formula.formulaErrors}
                          />
                          {formula.parameters && formula.parameters.length > 0 && (
                            <div>
                              <span className="text-bold mt5">{t('FORMULA.DOCUMENTATION.PARAMETERS')}: </span>{' '}
                              {formula.parameters.map((param) => `${param.identifier} (${param.name})`).join(', ')}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};
